import { useEffect, useRef } from 'react';
import { Plural, useLingui } from '@lingui/react/macro';

import Hide from '@rover/icons/svg/hide.svg';
import Show from '@rover/icons/svg/show.svg';
import { Button, Flex, Text } from '@rover/kibble/core';

type Props = {
  isManaging: boolean;
  selectedCount: number;
  isSaving: boolean;
  /** Set when the controls sit on a dark background, as they do in the lightbox. */
  contrast?: boolean;
  onEnterManageMode: () => void;
  onCancel: () => void;
  onMakePrivate: () => void;
  onMakePublic: () => void;
};

// Shown disabled, the bulk actions would read as a permission problem.
const ImageGridManageControls = ({
  isManaging,
  selectedCount,
  isSaving,
  contrast = false,
  onEnterManageMode,
  onCancel,
  onMakePrivate,
  onMakePublic,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const manageRef = useRef<HTMLElement>(null);
  const doneRef = useRef<HTMLElement>(null);
  const wasManaging = useRef(isManaging);

  // Switching modes replaces the pressed button, dropping keyboard focus.
  useEffect(() => {
    if (wasManaging.current === isManaging) return;
    wasManaging.current = isManaging;
    (isManaging ? doneRef : manageRef).current?.focus?.();
  }, [isManaging]);

  if (!isManaging) {
    return (
      <Flex flexDirection="row" flex="1" justifyContent="flex-end">
        <Button size="small" onPress={onEnterManageMode} webProps={{ ref: manageRef }}>
          {t`Manage photos`}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" flex="1" gap="3x">
      <Flex flexDirection="row" width="100%" justifyContent="flex-end">
        <Button
          variant="flat"
          size="small"
          contrast={contrast}
          onPress={onCancel}
          webProps={{ ref: doneRef }}
        >
          {t`Done`}
        </Button>
      </Flex>
      {selectedCount > 0 && (
        <Flex
          flexDirection="row"
          width="100%"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap="3x"
        >
          <Text
            size="200"
            fontWeight="semibold"
            textColor={contrast ? 'primary_inverse' : 'primary'}
          >
            <Plural value={selectedCount} one="# photo selected" other="# photos selected" />
          </Text>
          <Flex flexDirection="row" alignItems="center" gap="2x">
            <Button
              size="small"
              icon={Hide}
              loading={isSaving}
              disabled={isSaving}
              onPress={onMakePrivate}
            >
              {t`Make private`}
            </Button>
            <Button
              variant="primary"
              size="small"
              icon={Show}
              loading={isSaving}
              disabled={isSaving}
              onPress={onMakePublic}
            >
              {t`Make public`}
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default ImageGridManageControls;
